/**
 * 파일명 : evl0104p.tsx
 * 설명 : 인사평가 평가항목관리화면
 * 수정내역
 * **********************************************
 * Version    수정일자      수정자    수정내용
 * **********************************************
 * 1.0       2025-12-18   김한철    최초작성
 */

/************************************************
 * Import 영역
 ************************************************/
/*  AntD  */
import React, { useEffect, useMemo, useCallback, useRef, useState } from "react";
import {
  Form,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Typography,
  Row,
  Col,
  Space,
  Button,
  message,
  Alert
} from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ko";

/* 업무 */
import * as commonProxy from "libs/CommonProxy";
import * as common from "libs/common";

/* Redux */
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { Evl0104pAction } from "../../../redux/slice/evl/evl0104p_slice";

/************************************************
 * Type / Const
 ************************************************/
const { Title, Text } = Typography;

type Evl0104pPopupProps = any;

type EditFormValues = {
  evlCd?: string;
  itmTypCd?: string;
  dtlTypCd?: string;
  baseAsgn?: number | null;
  itmNm?: string;
  itmDesc?: string;
  useStDt?: dayjs.Dayjs;
  useEdDt?: dayjs.Dayjs;
  useYn?: string;
};

type SelectOption = { value: string; label: string; children?: SelectOption[]};

const toOptions = (arr?: Array<{ value: string; label: string; children?: any }>) =>
  (arr ?? []).map((n) => ({
    value: String(n.value),
    label: `${n.label}`,
    children: n.children,
  }));
  
/************************************************
 * 코드 트리 (기존)
 ************************************************/

type CodeNode = {
  value: string;
  label: string;
  children?: CodeNode[];
};

/**
 * EDIT 모드가 아닐 때(= 신규) 특정 path 노드를 숨기는 필터
 * path 형식: "01/03", "05/06" 등
 */
const EDIT_ONLY_PATHS = new Set<string>([
  "01/03", // 인사평가-책임이상 > 능력개발 및 업무개선사항
  "02/03", // 인사평가-선임이하 > 능력개발 및 업무개선사항
  "04/04", // 프로젝트평가-팀원 > 프로젝트 수행 내역
  "05/06", // 업무서약서 > 업무서약
]);

const filterCodeTreeByPath = (
  tree: CodeNode[],
  isEdit: boolean,
  editOnlyPaths: Set<string>
): CodeNode[] => {
  if (isEdit) return tree;

  const walk = (nodes: CodeNode[], parentPath = ""): CodeNode[] => {
    return nodes
      .filter((node) => {
        const path = parentPath ? `${parentPath}/${node.value}` : node.value;
        return !editOnlyPaths.has(path);
      })
      .map((node) => {
        const path = parentPath ? `${parentPath}/${node.value}` : node.value;
        const children = node.children ? walk(node.children, path) : undefined;
        return { ...node, children };
      })
      .filter((node) => !node.children || node.children.length > 0);
  };

  return walk(tree);
};

const CODE_TREE: CodeNode[] = [
  {
    value: "01",
    label: "인사평가 - 책임이상",
    children: [
      {
        value: "01",
        label: "업적",
        children: [
          { value: "01", label: "업적평가" },
          { value: "02", label: "업적기술" },
        ],
      },
      {
        value: "02",
        label: "역량",
        children: [
          { value: "03", label: "공통역량" },
          { value: "04", label: "역할역량" },
          { value: "05", label: "직무역량" },
        ],
      },
      {
        value: "03",
        label: "능력개발 및 업무개선사항",
        children: [
          { value: "06", label: "IT직무능력" },
          { value: "07", label: "업무직무능력" },
          { value: "08", label: "자기계발" },
          { value: "09", label: "업무개선" },
          { value: "10", label: "부하직원육성" },
          { value: "11", label: "기타" },
        ],
      },
    ],
  },
  {
    value: "02",
    label: "인사평가 - 선임이하",
    children: [
      {
        value: "01",
        label: "업적",
        children: [
          { value: "01", label: "업적평가" },
          { value: "02", label: "업적기술" },
        ],
      },
      {
        value: "02",
        label: "역량",
        children: [
          { value: "03", label: "공통역량" },
          { value: "04", label: "역할역량" },
          { value: "05", label: "직무역량" },
        ],
      },
      {
        value: "03",
        label: "능력개발 및 업무개선사항",
        children: [
          { value: "06", label: "IT직무능력" },
          { value: "07", label: "업무직무능력" },
          { value: "08", label: "자기계발" },
          { value: "09", label: "업무개선" },
          { value: "10", label: "부하직원육성" },
          { value: "11", label: "기타" },
        ],
      },
    ],
  },

  {
    value: "03",
    label: "프로젝트평가 - PM",
    children: [
      {
        value: "05",
        label: "프로젝트 수행 실적",
        children: [
          { value: "11", label: "기타" },
          { value: "13", label: "일정" },
          { value: "14", label: "인력" },
          { value: "15", label: "품질" },
          { value: "16", label: "비용" },
        ],
      },
    ],
  },
  {
    value: "04",
    label: "프로젝트평가 - 팀원",
    children: [
      {
        value: "04",
        label: "프로젝트 수행 내역",
        children: [{ value: "12", label: "프로젝트수행내역" }],
      },
      {
        value: "05",
        label: "프로젝트 수행 실적",
        children: [
          { value: "17", label: "공통평가" },
          { value: "18", label: "M/M" },
          { value: "19", label: "팀원" },
          { value: "20", label: "컨설턴트" },
        ],
      },
    ],
  },
  {
    value: "05",
    label: "업무서약서",
    children: [
      {
        value: "06",
        label: "업무서약",
        children: [{ value: "24", label: "담당직무부문" }],
      },
      {
        value: "07",
        label: "기타서약",
        children: [
          { value: "22", label: "팀기여부문" },
          { value: "23", label: "자기개발부문" },
        ],
      },
    ],
  },
];

/************************************************
 * ✅ 정해진 총점(엑셀 기준 하드코딩)  <-- 여기만 채움(필요한 부분)
 ************************************************/
const SCORE_CAP: Record<string, number> = {
  /**********************************************************************
   * 항목별 정해진 총점 (엑셀 기준)
   * key: EVL_CD|ITM_TYP_CD|DTL_TYP_CD
   *
   * EVL_CD
   *  01 = 인사평가-책임이상
   *  02 = 인사평가-선임이하
   *  03 = 프로젝트평가-PM
   *  04 = 프로젝트평가-팀원
   **********************************************************************/

  /* 02 인사평가-선임이하 */
  "02|01|01": 40, // 업적 > 업적평가
  "02|02|03": 20, // 역량 > 공통역량
  "02|02|04": 20, // 역량 > 역할역량
  "02|02|05": 20, // 역량 > 직무역량

  /* 01 인사평가-책임이상 */
  "01|01|01": 65, // 업적 > 업적평가
  "01|02|03": 13, // 역량 > 공통역량
  "01|02|04": 10, // 역량 > 역할역량
  "01|02|05": 12, // 역량 > 직무역량

  /* 03 프로젝트평가-PM */
  "03|05|11": 5, // 프로젝트수행실적 > 기타
  "03|05|13": 40, // 프로젝트수행실적 > 일정
  "03|05|14": 20, // 프로젝트수행실적 > 인력
  "03|05|15": 20, // 프로젝트수행실적 > 품질
  "03|05|16": 20, // 프로젝트수행실적 > 비용

  /* 04 프로젝트평가-팀원 */
  "04|05|17": 60, // 프로젝트수행실적 > 공통평가
  "04|05|18": 40, // 프로젝트수행실적 > M/M
  "04|05|19": 40, // 프로젝트수행실적 > 팀원
  "04|05|20": 40, // 프로젝트수행실적 > 컨설턴트
};

const makeCapKey = (evlCd?: string, itmTypCd?: string, dtlTypCd?: string) =>
  `${evlCd ?? ""}|${itmTypCd ?? ""}|${dtlTypCd ?? ""}`;

export default function Evl0104p(props: Evl0104pPopupProps) {
  const dispatch = useAppDispatch();
  const p: any = (props as any)?.props ?? props;

  const mode = useAppSelector((state) => state.Evl0104p.mode);
  const hrEvlItmId = useAppSelector((state) => state.Evl0104p.hrEvlItmId);
  const rowData = useAppSelector((state) => state.Evl0104p.rowData);

  const isEdit = mode === "EDIT";

  const [form] = Form.useForm<EditFormValues>();
  const initializingRef = useRef<boolean>(true);

  const openKey = useMemo(() => {
    const rid =
      p?.rowData?.hrEvlItmId ??
      p?.rowData?.HR_EVL_ITM_ID ??
      p?.hrEvlItmId ??
      "";
    return `${p?.mode ?? ""}|${rid}`;
  }, [p?.mode, p?.hrEvlItmId, p?.rowData]);

  const resetAll = useCallback(() => {
    initializingRef.current = true;

    form.resetFields();
    dispatch(Evl0104pAction.initState());

    requestAnimationFrame(() => {
      initializingRef.current = false;
    });
  }, [dispatch, form]);

  const close = useCallback(() => {
    resetAll();

    if (typeof p.cancel === "function") {
      p.cancel();
      return;
    }

    window.close();
    setTimeout(() => {
      if (!window.closed)
        message.info("이 창은 자동으로 닫을 수 없습니다. 창을 직접 닫아주세요.");
    }, 300);
  }, [p, resetAll]);

  useEffect(() => {
    dispatch(Evl0104pAction.initState());

    dispatch(
      Evl0104pAction.setContext({
        mode: p.mode,
        hrEvlItmId: p.hrEvlItmId,
        rowData: p.rowData,
      })
    );

    initializingRef.current = true;
    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  useEffect(() => {
    initializingRef.current = true;

    if (!isEdit) {
      form.resetFields();
      form.setFieldsValue({
        useYn: "Y",
        baseAsgn: 0,
        useStDt: dayjs(),
        useEdDt: dayjs("99991231", "YYYYMMDD"),
      });

      requestAnimationFrame(() => {
        initializingRef.current = false;
      });
      return;
    }

    const row = rowData;
    if (row) {
      const useSt = row.useStDt ?? row.USE_ST_DT ?? "";
      const useEd = row.useEdDt ?? row.USE_ED_DT ?? "";

      const rawUseEd = String(useEd ?? "").trim();
      const useEd8 = rawUseEd.replace(/\D/g, "").slice(0, 8);

      const useYn =
        useEd8 && useEd8 !== "99991231" && useEd8 !== "20991231" ? "N" : "Y";

      form.setFieldsValue({
        evlCd: row.evlCd ?? row.EVL_CD ?? "",
        itmTypCd: row.itmTypCd ?? row.ITM_TYP_CD ?? "",
        dtlTypCd: row.dtlTypCd ?? row.DTL_TYP_CD ?? "",
        itmNm: row.itmNm ?? row.ITM_NM ?? "",
        itmDesc: row.itmDesc ?? row.ITM_DESC ?? "",
        baseAsgn: row.baseAsgn ?? row.BASE_ASGN,
        useStDt: useSt
          ? dayjs(String(useSt).replace(/\D/g, "").slice(0, 8), "YYYYMMDD")
          : undefined,
        useEdDt: useEd
          ? dayjs(String(useEd).replace(/\D/g, "").slice(0, 8), "YYYYMMDD")
          : undefined,
        useYn,
      });
    }

    requestAnimationFrame(() => {
      initializingRef.current = false;
    });
  }, [form, isEdit, rowData]);

  const watchUseYn = Form.useWatch("useYn", form);

  useEffect(() => {
    if (!watchUseYn) return;
    if (initializingRef.current) return;

    if (watchUseYn === "Y") {
      form.setFieldsValue({ useEdDt: dayjs("99991231", "YYYYMMDD") });
    } else {
      form.setFieldsValue({ useEdDt: dayjs() }); // 오늘
    }
  }, [watchUseYn, form]);


  const watchEvlCd = Form.useWatch("evlCd", form);
  const watchItmTypCd = Form.useWatch("itmTypCd", form);
  const watchDtlTypCd = Form.useWatch("dtlTypCd", form);
  const watchBaseAsgn = Form.useWatch("baseAsgn", form);

  const [totalAsgn, setTotalAsgn] = React.useState<number>(0);

  const [hrEvlStDt, setHrEvlStDt] = useState<string>("");
  const [bizPlgStDt, setBizPlgStDt] = useState<string>("");

  useEffect(() => {
    if (isEdit) return;
    form.setFieldsValue({
      itmTypCd: undefined,
      dtlTypCd: undefined,
      baseAsgn: undefined,
      itmNm   : undefined
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchEvlCd, isEdit]);

  useEffect(() => {
    if (isEdit) return;
    form.setFieldsValue({ dtlTypCd: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchItmTypCd, isEdit]);

// ✅ 1) 평가구분(최상위) 옵션: 신규(isEdit=false)면 숨김 적용
const filteredTree = useMemo(
  () => filterCodeTreeByPath(CODE_TREE, isEdit, EDIT_ONLY_PATHS),
  [isEdit]
);

  const evlOpts = useMemo<SelectOption[]>(
    () => toOptions(filteredTree),
    [filteredTree]
  );

  // ✅ 2) 항목유형 옵션: 필터된 트리 기준으로 찾기
  const itmTypOpts = useMemo<SelectOption[]>(() => {
    const evlCd = String(watchEvlCd ?? "");
    const evlNode = filteredTree.find((x) => x.value === evlCd);
    return toOptions(evlNode?.children);
  }, [filteredTree, watchEvlCd]);

  // ✅ 3) 상세구분 옵션: 필터된 트리 기준으로 찾기
  const dtlTypOpts = useMemo<SelectOption[]>(() => {
    const evlCd = String(watchEvlCd ?? "");
    const itmCd = String(watchItmTypCd ?? "");

    const evlNode = filteredTree.find((x) => x.value === evlCd);
    const itmNode = evlNode?.children?.find((x) => x.value === itmCd);

    return toOptions(itmNode?.children);
  }, [filteredTree, watchEvlCd, watchItmTypCd]);

  // ✅ 업무서약서 여부
  const isWorkPledge = useMemo(() => {
    const v = String(watchEvlCd ?? "");
    return v === "05" || v === "5";
  }, [watchEvlCd]);

  // ✅ (추가) 배점 입력 불필요 예외 조합: EVL=01, ITM=01, DTL=02
  const isNoScoreByCombo = useMemo(() => {
    const evl = String(watchEvlCd ?? "");
    const itm = String(watchItmTypCd ?? "");
    const dtl = String(watchDtlTypCd ?? "");
    return evl === "01" && itm === "01" && dtl === "02";
  }, [watchEvlCd, watchItmTypCd, watchDtlTypCd]);


  const fetchTotalAsgn = useCallback(() => {
    const evlCd = String(form.getFieldValue("evlCd") ?? "");
    const itmTypCd = String(form.getFieldValue("itmTypCd") ?? "");
    const dtlTypCd = String(form.getFieldValue("dtlTypCd") ?? "");

    if (!evlCd || !itmTypCd || !dtlTypCd) {
      setTotalAsgn(0);
      return;
    }
    
    const requestData = {
      evlCd,
      itmTypCd,
      dtlTypCd,
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0024",
      success: (responseData: any) => {
        setTotalAsgn(Number(responseData.totalAsgn) || 0);
      },
    });
  }, [form]);

  useEffect(() => {
    fetchTotalAsgn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchEvlCd, watchItmTypCd, watchDtlTypCd]);

  const loadEvlCycleInfo = useCallback(() => {
  const evlYear = String(new Date().getFullYear()); // ✅ 항상 올해 YYYY

  const requestData = { evlYear };

  commonProxy.callAsyncSvc(requestData, {
    txCd: "TREVLR0003",
    success: (responseData: any) => {
      // ✅ 응답 구조가 data 래핑일 수도 있어서 둘 다 대응
      const data = responseData?.data ?? responseData;

      const first = data?.EvlCycleItems?.[0];
      const rawHr = first?.hrEvlStDt ?? "";
      const rawBiz = first?.bizPlgStDt ?? "";

      const formatYmd = (ymd?: string) => {
        if (!ymd || ymd.length !== 8) return "";
        return `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`;
      };

      setHrEvlStDt(formatYmd(rawHr));
      setBizPlgStDt(formatYmd(rawBiz));
    },
  });
}, []) ;

  useEffect(() => {
    loadEvlCycleInfo();
  }, [loadEvlCycleInfo]);

  const onClickSave = useCallback(async () => {
    try {
      const v = await form.validateFields();

      // ✅ (수정) 저장 payload 배점 규칙
      // - 업무서약서: null
      // - 예외조합: 0 고정
      // - 일반: 입력값
      const baseAsgnToSend = isWorkPledge
        ? null
        : isNoScoreByCombo
        ? 0
        : v.baseAsgn;

      const payload: any = {
        evlCd: v.evlCd,
        itmTypCd: v.itmTypCd,
        dtlTypCd: v.dtlTypCd,

        itmNm: v.itmNm,
        itmDesc: v.itmDesc ?? "",
        baseAsgn: baseAsgnToSend,

        hrEvlItmId: isEdit ? (hrEvlItmId ?? "") : "",

        useStDt: v.useStDt ? v.useStDt.format("YYYYMMDD") : "",
        useEdDt: v.useEdDt ? v.useEdDt.format("YYYYMMDD") : "99991231",

        useYn: v.useYn,
        mode,
      };

      await commonProxy.callAsyncSvc(payload, {
        txCd: "TREVLS0007",
        success: (responseData: any) => {
          // ✅ 차단 케이스: res 자체에 lockYn이 바로 옴
          if (responseData?.lockYn === "Y") {
            message.error(responseData?.message || "저장할 수 없습니다.");
            return;
          }

          // ✅ 혹시 일반 실패가 success/message로 오는 tx도 있으니 방어
          if (responseData?.success === false) {
            message.error(responseData?.message || "저장 실패");
            return;
          }
          
          message.success("저장 완료");

          const savedId =
            responseData?.hrEvlItmId ?? responseData?.data?.hrEvlItmId ?? payload.hrEvlItmId;

          p.accept?.({ success: true, mode, hrEvlItmId: savedId });

          resetAll();
          close();
        },
      });
    } catch (e) {}
  }, [
    close,
    form,
    hrEvlItmId,
    isEdit,
    mode,
    p,
    resetAll,
    isWorkPledge,
    isNoScoreByCombo,
  ]);

  const onClick = useCallback(
    (e: any) => {
      const buttonName = e?.currentTarget?.name;
      if (buttonName === "btnClose") close();
      if (buttonName === "btnOk") onClickSave();
    },
    [close, onClickSave]
  );

  const codeFieldsDisabled = isEdit;
  // 코드 상단 (컴포넌트 내부)
  const evlCd = form.getFieldValue("evlCd");
  const itmTypCd = form.getFieldValue("itmTypCd");
  const dtlTypCd = form.getFieldValue("dtlTypCd");

  const scoreKey = `${evlCd}|${itmTypCd}|${dtlTypCd}`;
  const scoreCap = SCORE_CAP[scoreKey];
  // ✅ (수정) 업무서약서 + 예외조합 모두 배점 비활성화
  const baseAsgnDisabled = scoreCap === undefined;

  // ✅ 현재총점/정해진총점 비교 표시용
  const capKey = makeCapKey(watchEvlCd, watchItmTypCd, watchDtlTypCd);
  const capScore = SCORE_CAP[capKey]; // 없으면 undefined

  const inputAsgn = Number(watchBaseAsgn ?? 0);
  const oldAsgn = Number(
    (rowData as any)?.baseAsgn ?? (rowData as any)?.BASE_ASGN ?? 0
  );

  const afterAsgn = isEdit
    ? (Number(totalAsgn) || 0) - oldAsgn + inputAsgn
    : (Number(totalAsgn) || 0) + inputAsgn;

  const isOverCur =
    typeof capScore === "number" && (Number(totalAsgn) || 0) > capScore;
  const isOverAfter = typeof capScore === "number" && afterAsgn > capScore;

  const itmTypDisabled = codeFieldsDisabled || !watchEvlCd;
  const dtlTypDisabled = codeFieldsDisabled || !watchEvlCd || !watchItmTypCd;

  // ✅ 공통: 그리드 칸에서 컨트롤은 100%로
  const controlFull = { width: "100%" as const };

  const isHrEval = evlCd === "01" || evlCd === "02";
    // ⭐ 라벨 동적 변경
  const itemNameLabel = isHrEval ? "항목명" : "카테고리";
  const itemDescLabel = isHrEval ? "설명" : "항목명";

  // ✅ 카테고리 입력 disabled 조건 (인사평가 제외 + 상세유형 선택된 경우)
const categoryDisabled = !isHrEval && !!dtlTypCd;

  const selectedDtlTypLabel = useMemo(() => {
  const v = String(watchDtlTypCd ?? "");
  return dtlTypOpts.find((o) => String(o.value) === v)?.label;
  }, [dtlTypOpts, watchDtlTypCd]);

  // ✅ 상세유형 선택 시 카테고리(itmNm)에 value 자동 입력
  useEffect(() => {
    if (isHrEval) return; // 인사평가는 기존 유지
    


    if (dtlTypCd) {
      // 상세유형 선택됨 → 카테고리에 value 자동 세팅
      form.setFieldsValue({ itmNm: selectedDtlTypLabel ?? String(watchDtlTypCd), });
    } else {
      // 상세유형 해제됨 → 카테고리 비우고 다시 입력 가능하게
      form.setFieldsValue({ itmNm: undefined });
    }
  }, [isHrEval, dtlTypCd, form]);

  return (
    <div style={{ padding: 20 }}>
      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        {/* 헤더 */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            {isEdit ? "평가 항목 수정" : "평가 항목 신규"}
          </Title>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <span>
                  인사평가 시작일: <b>{hrEvlStDt || "미확정"}</b>
                </span>
                <span>
                  업무서약 시작일: <b>{bizPlgStDt || "미확정"}</b>
                </span>
              </div>  
        </div>
            <Alert type="warning" showIcon message="평가가 시작된 이후에는 평가항목 수정 및 저장이 불가능합니다."/>

        <Form form={form} layout="vertical" preserve={false}>
          {/* ✅ 1행: 코드 3개 */}
          <Row gutter={[12, 0]} align="top">
            <Col xs={24} sm={24} md={8} lg={8}>
              <Form.Item
                name="evlCd"
                label="평가구분"
                rules={[{ required: true, message: "평가구분을 선택하세요" }]}
              >
                <Select
                  style={controlFull}
                  options={evlOpts}
                  disabled={codeFieldsDisabled}
                  showSearch
                  optionFilterProp="label"
                  popupMatchSelectWidth
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={8} lg={8}>
              <Form.Item
                name="itmTypCd"
                label="항목유형"
                rules={[{ required: true, message: "항목유형을 선택하세요" }]}
              >
                <Select
                  style={controlFull}
                  options={itmTypOpts}
                  disabled={itmTypDisabled}
                  showSearch
                  optionFilterProp="label"
                  popupMatchSelectWidth
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={8} lg={8}>
              <Form.Item
                name="dtlTypCd"
                label="상세유형"
                rules={[{ required: true, message: "상세유형을 선택하세요" }]}
              >
                <Select
                  style={controlFull}
                  options={dtlTypOpts}
                  disabled={dtlTypDisabled}
                  showSearch
                  optionFilterProp="label"
                  popupMatchSelectWidth
                />
              </Form.Item>
            </Col>
          </Row>

          {/* ✅ 2행: 기본배점 */}
          <Row gutter={[12, 0]} align="top">
            <Col xs={24} sm={12} md={8} lg={20}>
              <Form.Item
                name="baseAsgn"
                label="기본배점"
                extra={
                  baseAsgnDisabled
                    ? "해당 항목은 배점을 입력할 수 없습니다."
                    : `최대 배점: ${scoreCap}점`
                }
                rules={
                  baseAsgnDisabled
                    ? []
                    : [{ required: true, message: "기본배점을 입력하세요" }]
                }
              >
                <InputNumber
                  style={{ width: 70 }}
                  disabled={baseAsgnDisabled}
                  max={scoreCap}
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* ✅ 현재총점 / 정해진총점 (배점 입력 대상일 때만 표시) */}
          {!baseAsgnDisabled && watchEvlCd && watchItmTypCd && watchDtlTypCd && (
            <Row
              gutter={[12, 0]}
              align="top"
              style={{ marginTop: -8, marginBottom: 8 }}
            >
              <Col xs={24}>
                <Space direction="vertical" size={2}>
                  <Text type="secondary">
                    현재 총점 : {" "}
                    <Text strong type={isOverCur ? "danger" : undefined}>
                      {totalAsgn}
                    </Text>
                    {" / "}
                    총점 : {" "}
                    <Text strong>
                      {typeof capScore === "number" ? capScore : "-"}
                    </Text>
                    {isOverCur && <Text type="danger"> (초과)</Text>}
                  </Text>

                  <Text type="secondary">
                    저장 후 : {" "}
                    <Text strong type={isOverAfter ? "danger" : undefined}>
                      {afterAsgn}
                    </Text>
                    {" / "}
                    <Text strong>
                      {typeof capScore === "number" ? capScore : "-"}
                    </Text>
                    {isOverAfter && <Text type="danger"> (초과)</Text>}
                  </Text>
                </Space>
              </Col>
            </Row>
          )}

          {/* 항목명 */}
          <Form.Item
            name="itmNm"
            label={itemNameLabel}
            rules={[
              { required: true, message: "항목명을 입력하세요" },
              { max: 100, message: "항목명은 100자 이내" },
            ]}
          >
            <Input style={controlFull} disabled={categoryDisabled} placeholder="예: 업적 목표 달성"/>
          </Form.Item>

          {/* 설명 */}
          <Form.Item
            name="itmDesc"
            label={itemDescLabel}
            rules={
              [
                { required: true, message: "설명을 입력하세요" },
                { max: 500, message: "설명은 500자 이내" }
              ]
            }
          >
            <Input.TextArea placeholder="설명" rows={3} />
          </Form.Item>

          {/* ✅ 날짜/상태 */}
          <Row gutter={[12, 0]} align="top">
            <Col xs={24} sm={12} md={6} lg={6}>
              <Form.Item name="useStDt" label="사용 시작일시">
                <DatePicker style={controlFull} format="YYYY-MM-DD" disabled/>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6} lg={6}>
              <Form.Item
                name="useEdDt"
                label="사용 종료일시"
              >
                <DatePicker
                  style={controlFull}
                  format="YYYY-MM-DD"
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6} lg={6}>
              <Form.Item name="useYn" label="사용 여부" rules={[{ required: true }]}>
                <Select
                  style={controlFull}
                  options={[
                    { value: "Y", label: "사용" },
                    { value: "N", label: "종료" },
                  ]}
                />
              </Form.Item>
            </Col>

            
          </Row>

          {/* ✅ 버튼 */}
          <Row justify="end" gutter={[8, 8]}>
            <Col>
              <Button name="btnClose" onClick={onClick}>
                닫기
              </Button>
            </Col>
            <Col>
              <Button type="primary" name="btnOk" onClick={onClick}>
                저장
              </Button>
            </Col>
          </Row>
        </Form>
      </Space>
    </div>
  );
}
